import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Alert,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faFileExcel,
  faFileInvoice,
  faExclamationTriangle,
  faCheckCircle,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import DownloadFormatDropdown, {
  DOWNLOAD_FORMATS,
} from "../common/DownloadFormatDropdown";
import FalconDropzone from "../common/FalconDropzone";
import { Toast } from "./../UI/toast/Toast";
import {
  exportPropertiesToExcel,
  importPropertiesFromExcel,
} from "../../helpers/api/condominium";
import {
  importAdminPaymentsFromExcel,
  importAccountsReceivableFromExcel,
} from "../../helpers/api/finance";
import {
  exportProvidersToExcel,
  importProvidersFromExcel,
} from "../../helpers/api/provider";

const ImportPropertiesModal = ({ isOpen, toggle, importType, selectedCondominium, onSuccess }) => {
  const [importFile, setImportFile] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importErrors, setImportErrors] = useState(null);
  const [importSuccess, setImportSuccess] = useState(null);
  const [importSummary, setImportSummary] = useState(null);
  // HO-TASK-249: formato de las dos plantillas de cuentas por cobrar, que se
  // bajan desde tarjetas y no desde un botón con desplegable.
  const [templateFormat, setTemplateFormat] = useState("xlsx");

  const handleToggle = () => {
    setImportErrors(null);
    setImportSuccess(null);
    setImportSummary(null);
    setImportFile([]);
    toggle();
  };

  const triggerDownload = (href, filename) => {
    const link = document.createElement("a");
    link.href = href;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /**
   * HO-ISSUE-118: para propietarios no se baja una plantilla vacía sino el
   * estado actual del condominio. La carga completa las unidades que ya
   * existen (no las crea), así que un archivo en blanco no sirve para
   * arrancar; y bajando lo que hay, el Inmueble de cada fila viaja escrito,
   * que es la clave con la que el backend vuelve a encontrar cada unidad.
   */
  const exportCurrentProperties = async (format = "xlsx") => {
    setExporting(true);
    try {
      const blob = await exportPropertiesToExcel(selectedCondominium, format);
      const href = URL.createObjectURL(blob);
      triggerDownload(href, `propietarios.${format}`);
      URL.revokeObjectURL(href);
    } catch (error) {
      Toast.fire({
        type: "error",
        title:
          error?.data?.error ||
          error?.data?.message ||
          "No se pudo descargar el archivo de propietarios.",
        timer: 5000,
      });
    } finally {
      setExporting(false);
    }
  };

  const exportCurrentProviders = async (format = "xlsx") => {
    setExporting(true);
    try {
      const blob = await exportProvidersToExcel(selectedCondominium, format);
      const href = URL.createObjectURL(blob);
      triggerDownload(href, `proveedores.${format}`);
      URL.revokeObjectURL(href);
    } catch (error) {
      Toast.fire({
        type: "error",
        title:
          error?.data?.error ||
          error?.data?.message ||
          "No se pudo descargar el archivo de proveedores.",
        timer: 5000,
      });
    } finally {
      setExporting(false);
    }
  };

  // HO-TASK-249: cada plantilla existe en los tres formatos que acepta la
  // carga. El nombre del archivo en public/ y el que se le ofrece al
  // administrador no siempre coinciden, así que van por separado.
  const TEMPLATES = {
    advance: { file: "abonos", download: "abonos" },
    receivable: { file: "expenses", download: "expenses" },
    "receivable-with-periods": {
      file: "expenses_with_periods",
      download: "expenses_con_periodos",
    },
  };

  const downloadTemplate = (type = "properties", format = "xlsx") => {
    const template = TEMPLATES[type];

    if (!template) return;

    // Rutas relativas al propio frontend (las plantillas viven en public/,
    // Vite las copia a la raíz del build) — antes usaban VITE_URL, la URL del
    // backend, donde estos archivos no existen, y al ser de otro origen el
    // navegador ignora el atributo download y termina navegando a la
    // respuesta de error.
    triggerDownload(
      `/${template.file}.${format}`,
      `${template.download}.${format}`
    );
  };

  const handleImport = async () => {
    if (importFile.length === 0) {
      Toast.fire({ type: "error", title: "Por favor seleccione un archivo." });
      return;
    }

    setLoading(true);
    setImportErrors(null);
    setImportSuccess(null);
    setImportSummary(null);

    try {
      const formData = new FormData();
      formData.append("file", importFile[0].file);

      Toast.fire({ type: "info", title: "Cargando archivo..." });

      let response;
      if (importType === "properties") {
        response = await importPropertiesFromExcel(selectedCondominium, formData);
      } else if (importType === "advance") {
        response = await importAdminPaymentsFromExcel(selectedCondominium, formData);
      } else if (importType === "receivable") {
        response = await importAccountsReceivableFromExcel(selectedCondominium, formData);
      } else if (importType === "providers") {
        response = await importProvidersFromExcel(selectedCondominium, formData);
      }

      setImportSuccess(response.message || "Importación completada exitosamente.");
      if (response.resumen) setImportSummary(response.resumen);
      setImportFile([]);
      if (onSuccess) onSuccess();
    } catch (error) {
      if (error?.data?.detalles && Array.isArray(error.data.detalles)) {
        setImportErrors({
          summary: error.data.error || "Se encontraron errores en la importación.",
          rows: error.data.detalles,
        });
      } else if (error?.data?.errors && Array.isArray(error.data.errors)) {
        setImportErrors({
          summary: error.data.message || error.data.error || "Se encontraron errores en la importación.",
          rows: error.data.errors.map((e, i) => {
            const match = /^Fila (\d+):\s*(.*)$/s.exec(String(e));
            return match
              ? { fila: Number(match[1]), inmueble: "", errores: [match[2]] }
              : { fila: i + 2, inmueble: "", errores: [e] };
          }),
        });
      } else {
        Toast.fire({
          type: "error",
          title: error?.data?.error || error?.data?.message || "Error al importar.",
          timer: 5000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleToggle}
      size={importErrors || importSummary?.detalle_omitidos?.length > 0 ? "lg" : "md"}
    >
      <ModalHeader toggle={handleToggle}>
        {importType === "properties"
          ? "Importar Propietarios"
          : importType === "advance"
            ? "Importar Saldos a Favor Masivos"
            : importType === "providers"
              ? "Importar Proveedores"
              : "Importar Cuentas por Cobrar"}
      </ModalHeader>
      <ModalBody>
        {importSuccess && (
          <Alert color="success" className="d-flex align-items-start mb-3">
            <FontAwesomeIcon icon={faCheckCircle} className="mt-1 mr-2 flex-shrink-0" />
            <span>{importSuccess}</span>
          </Alert>
        )}

        {importSummary?.detalle_omitidos?.length > 0 && (
          <div className="mb-3 rounded border border-info overflow-hidden">
            <div className="d-flex align-items-center bg-soft-info px-3 py-2 border-bottom border-info">
              <FontAwesomeIcon icon={faInfoCircle} className="text-info mr-2 flex-shrink-0" />
              <span className="fs--1 font-weight-semi-bold text-info mb-0">
                {importSummary.omitidos} fila(s) omitida(s): el inmueble ya tenía una deuda registrada para ese período
              </span>
            </div>
            <div style={{ maxHeight: "200px", overflowY: "auto" }} className="px-3 py-2">
              {importSummary.detalle_omitidos.map((row, idx) => (
                <div key={idx} className={idx > 0 ? "mt-1 pt-1 border-top" : ""}>
                  <p className="mb-0 fs--1 text-700">
                    <span className="font-weight-semi-bold text-800">Fila {row.fila} — {row.inmueble}</span>
                    {row.periodo ? ` · ${row.periodo}` : ""}
                  </p>
                </div>
              ))}
            </div>
            <p className="fs--1 text-700 mb-0 px-3 py-2 border-top border-info">
              Si alguna de esas deudas está mal, anúlala desde el menú ⋯ de la unidad con “Anular deuda inicial” y vuelve
              a subir el archivo.
            </p>
          </div>
        )}

        {importErrors && (
          <div className="mb-3">
            <Alert color="danger" className="d-flex align-items-start mb-2">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mt-1 mr-2 flex-shrink-0" />
              <span>{importErrors.summary}</span>
            </Alert>
            <div
              style={{
                maxHeight: "260px",
                overflowY: "auto",
                border: "1px solid #f5c2c7",
                borderRadius: "0.375rem",
                backgroundColor: "#fff8f8",
                padding: "0.75rem",
              }}
            >
              {importErrors.rows.map((row, idx) => (
                <div key={idx} className={idx > 0 ? "mt-2 pt-2 border-top border-danger border-opacity-25" : ""}>
                  <p className="mb-1 fw-semibold fs--1 text-danger">
                    Fila {row.fila}{row.inmueble || row.proveedor ? ` — ${row.inmueble || row.proveedor}` : ""}
                  </p>
                  <ul className="mb-0 ps-3">
                    {row.errores.map((e, ei) => (
                      <li key={ei} className="fs--1 text-dark">{e}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p className="fs--1 text-muted mt-2 mb-0">
              Corrija los errores en el archivo y vuelva a importarlo.
            </p>
          </div>
        )}

        {!importSuccess && (
          <>
            <div className="mb-3">
              <p className="fs--1 font-weight-semi-bold mb-2">
                {importType === "properties"
                  ? "Descarga el archivo con los propietarios como están hoy, complétalo y vuelve a subirlo:"
                  : importType === "providers"
                    ? "Descarga el archivo con los proveedores que ya tienes, complétalo y vuelve a subirlo:"
                    : "Descargue la plantilla para el tipo de importación:"}
              </p>
              {importType === "receivable" ? (
                <>
                  {/* HO-TASK-249: acá la descarga son dos tarjetas y no un
                  botón, así que el formato se elige una vez arriba y vale
                  para la que se pulse. */}
                  <FormGroup className="d-flex align-items-center mb-2">
                    <Label
                      for="receivableTemplateFormat"
                      className="fs--1 mb-0 mr-2 text-nowrap"
                    >
                      Formato:
                    </Label>
                    <Input
                      id="receivableTemplateFormat"
                      type="select"
                      bsSize="sm"
                      value={templateFormat}
                      onChange={(event) => setTemplateFormat(event.target.value)}
                      style={{ width: "auto" }}
                    >
                      {DOWNLOAD_FORMATS.map(({ format, label }) => (
                        <option key={format} value={format}>
                          {label}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                  {/* Son botones de verdad y no divs con role="button": así
                  traen puestos el relieve, el hover, el foco y el Enter del
                  tema, que es lo que hacía falta para que se lean como algo
                  que se pulsa. whiteSpace normal porque los botones de
                  Bootstrap no parten la línea, y acá debajo del título va una
                  descripción. */}
                  <div className="d-flex flex-column flex-sm-row">
                    <Button
                      color="falcon-default"
                      onClick={() => downloadTemplate("receivable", templateFormat)}
                      className="flex-fill text-left p-2 mb-2 mb-sm-0 mr-sm-2"
                      style={{ whiteSpace: "normal" }}
                    >
                      <div className="d-flex align-items-center text-info font-weight-semi-bold fs--1">
                        <FontAwesomeIcon icon={faDownload} className="mr-2" />
                        Carga acumulada
                      </div>
                      <p className="fs--2 text-600 mb-0 mt-1">
                        Se cargara el acumulado de la deuda de cada propietario
                      </p>
                    </Button>
                    <Button
                      color="falcon-default"
                      onClick={() => downloadTemplate("receivable-with-periods", templateFormat)}
                      className="flex-fill text-left p-2"
                      style={{ whiteSpace: "normal" }}
                    >
                      <div className="d-flex align-items-center text-info font-weight-semi-bold fs--1">
                        <FontAwesomeIcon icon={faDownload} className="mr-2" />
                        Carga detallada
                      </div>
                      <p className="fs--2 text-600 mb-0 mt-1">
                        Se cargara detallado la deuda de cada mes de cada propietario
                      </p>
                    </Button>
                  </div>
                </>
              ) : importType === "properties" ? (
                <DownloadFormatDropdown
                  label={
                    exporting ? "Descargando..." : "Descargar Propietarios Actuales"
                  }
                  disabled={exporting}
                  onSelect={exportCurrentProperties}
                />
              ) : importType === "providers" ? (
                <DownloadFormatDropdown
                  label={
                    exporting ? "Descargando..." : "Descargar Proveedores Actuales"
                  }
                  disabled={exporting}
                  onSelect={exportCurrentProviders}
                />
              ) : (
                <DownloadFormatDropdown
                  label="Descargar Plantilla"
                  onSelect={(format) => downloadTemplate(importType, format)}
                />
              )}
            </div>
            <FalconDropzone
              files={importFile}
              onChange={(files) => {
                setImportFile(files);
                setImportErrors(null);
              }}
              multiple={false}
              preview
              accept=".xlsx, .xls, .csv, .txt"
              placeholder={
                <>
                  <div className="fs-2 mb-2">
                    <FontAwesomeIcon
                      icon={importType === "receivable" ? faFileInvoice : faFileExcel}
                    />
                  </div>
                  <p className="fs--1 mb-0">Arrastre su archivo aquí o haga clic</p>
                </>
              }
            />
            <div className="mt-3">
              <p className="fs--1 text-muted mb-0 font-weight-semi-bold">
                Detalles de la importación:
              </p>
              {/* HO-TASK-249: la carga ya no es solo de Excel, así que se dice
              qué más se puede subir. Va arriba del detalle de columnas porque
              es lo primero que se pregunta quien trae el archivo de otro
              sistema. */}
              <p className="fs--1 text-muted mb-1">
                Formatos aceptados: Excel (.xlsx, .xls) y texto (.csv, .txt).
              </p>
              <p className="fs--1 text-muted mb-0">
                {importType === "properties"
                  ? (
                      <>
                        No modifiques la columna <strong>Usuario</strong>: es la que identifica cada unidad. El resto lo puedes cambiar, incluido el Inmueble, y se actualiza en la unidad que corresponde.
                        <span className="d-block mt-1">
                          <strong>Nombres</strong>, <strong>Apellidos</strong> e <strong>Inmueble</strong> son obligatorias; Cédula, Teléfono y Correo son opcionales. El orden no importa: las columnas se identifican por su nombre.
                        </span>
                        <span className="d-block text-primary font-weight-bold">
                          📌 La carga completa las unidades que ya tiene el condominio, no crea unidades nuevas. Si alguna fila no corresponde a ninguna unidad, no se carga nada y se te indica qué filas corregir.
                        </span>
                      </>
                    )
                  : importType === "advance"
                    ? "Para saldos a favor masivos: Identificador de Propiedad (requerido), Monto (requerido), Referencia (opcional), Banco (opcional)."
                    : importType === "providers"
                      ? (
                          <>
                            <strong>Nombre</strong> es obligatoria; Cédula o RIF, Descripción, Correo, Teléfono y Dirección son opcionales. El orden no importa: las columnas se identifican por su nombre.
                            <span className="d-block text-primary font-weight-bold">
                              📌 Cada proveedor se busca por su Cédula o RIF, o por su nombre si no lo trae: si ya existe se actualiza y si no, se crea. Si alguna fila tiene errores, no se carga nada y se te indica qué filas corregir.
                            </span>
                          </>
                        )
                      : (
                        <>
                          Para Cuentas por Cobrar hay dos formatos posibles, según la plantilla que use:
                          <span className="d-block mt-1">
                            <strong>Carga acumulada:</strong> Nombre del inmueble, Nombre del gasto, Monto del gasto y Descripcion (opcional).
                          </span>
                          <span className="d-block text-primary font-weight-bold">
                            📌 Se cargara el acumulado de la deuda de cada propietario.
                          </span>
                          <span className="d-block mt-1">
                            <strong>Carga detallada:</strong> Nombre del inmueble, Nombre del periodo, Fecha inicio, Fecha fin, Nombre del gasto, Monto del gasto y Descripcion (opcional).
                          </span>
                          <span className="d-block text-primary font-weight-bold">
                            📌 Se cargara detallado la deuda de cada mes de cada propietario
                          </span>
                        </>
                      )}
              </p>
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleToggle} disabled={loading}>
          {importSuccess ? "Cerrar" : "Cancelar"}
        </Button>
        {!importSuccess && (
          <Button
            color="primary"
            onClick={handleImport}
            disabled={importFile.length === 0 || loading}
          >
            {loading ? "Importando..." : "Importar"}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

ImportPropertiesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  importType: PropTypes.string.isRequired,
  selectedCondominium: PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
};

export default ImportPropertiesModal;
